import { CompanyInfo, ServiceItem, NavigationLink } from './types';

export const COMPANY_INFO: CompanyInfo = {
  name: "A2 Innovations Inc.",
  address: "3950 14th Ave",
  suite: "Suite 103",
  city: "Markham, ON",
  postalCode: "L3R 0A9",
  domain: "a2innovationsinc.com",
  fullAddress: "3950 14th Ave, Suite 103, Markham ON L3R 0A9"
};

export const NAVIGATION_LINKS: NavigationLink[] = [
  { label: "Home", href: "#" },
  { label: "Services", href: "#services" },
  { label: "About Us", href: "#about" },
  { label: "Contact", href: "#contact" },
];

export const SERVICES: ServiceItem[] = [
  {
    id: "workday-hcm",
    title: "Workday Human Capital Management",
    description: "Architecting the foundation of your workforce with Core HCM, Benefits, and Talent Management.",
    longDescription: "Your people are your competitive advantage. Our Workday HCM practice focuses on configuring the system to mirror your unique organizational DNA. We specialize in Core HCM, Onboarding, Absence Management, Benefits, and Advanced Compensation. We ensure that your data structure supports not just HR operations, but strategic decision-making. Whether you are deploying Workday for the first time or expanding your footprint with new modules, our consultants ensure a seamless employee experience from hire to retire.",
    features: [
      {
        title: "Core HCM & Supervisory Organization Design",
        description: "We configure your supervisory hierarchy to accurately reflect reporting structures, ensuring security and business processes flow correctly."
      },
      {
        title: "Benefits & Absence Management",
        description: "Automate open enrollment, manage complex eligibility rules, and streamline time-off plans for a better employee experience."
      },
      {
        title: "Advanced Compensation & Merit Planning",
        description: "Design and implement comprehensive compensation packages, merit cycles, and bonus structures that drive retention."
      },
      {
        title: "Talent & Performance Management",
        description: "Align individual goals with organizational strategy through robust performance reviews and talent calibration."
      },
      {
        title: "Staffing Models & Job Architecture",
        description: "Define how positions are created, filled, and managed to support scalable growth and accurate headcount reporting."
      }
    ],
    icon: "users",
    imageUrl: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=2000"
  },
  {
    id: "payroll-implementation",
    title: "Workday Payroll Implementation",
    description: "Precision-driven payroll deployment tailored to complex compliance and regulatory needs.",
    longDescription: "Our implementation methodology is built on years of experience delivering successful Workday Payroll go-lives for complex organizations in the US, Canada, and the UK. We align the system with your business processes to ensure long-term sustainability. From the initial 'Plan' phase through 'Architect', 'Configure & Prototype', 'Test', and 'Deploy', our consultants act as true partners. We place a heavy emphasis on Knowledge Transfer (KT) throughout the project, ensuring your internal team is empowered.",
    features: [
      {
        title: "Process Analysis & Design",
        description: "We map your current state payroll processes to Workday best practices to identify gaps and optimization opportunities."
      },
      {
        title: "Calculation Engine Configuration",
        description: "Configure robust earnings, deductions, and tax rules to handle even the most complex collective agreements."
      },
      {
        title: "Parallel Testing Strategy",
        description: "Rigorous comparison of legacy system outputs against Workday to ensure 100% accuracy before go-live."
      },
      {
        title: "Data Conversion & Validation",
        description: "Secure migration of historical payroll data with comprehensive validation frameworks to ensure integrity."
      },
      {
        title: "Cutover Planning",
        description: "Detailed minute-by-minute cutover plans and hypercare support to ensure a smooth transition to production."
      }
    ],
    icon: "layers",
    imageUrl: "https://images.unsplash.com/photo-1531403009284-440f080d1e12?auto=format&fit=crop&q=80&w=2000"
  },
  {
    id: "post-production-support",
    title: "Application Management Services (AMS)",
    description: "Ongoing support, troubleshooting, and release management for your entire Workday tenant.",
    longDescription: "Once you are live, the real journey begins. Our Application Management Services (AMS) provide you with flexible, on-demand expertise to handle the daily challenges of HR and Payroll operations. Whether it's troubleshooting a payroll error, managing biannual Workday feature releases, or adjusting Security policies, A2 Innovations acts as an extension of your team. We offer tiered support models to fit your budget, covering HCM, Payroll, Time Tracking, and Financials.",
    features: [
      {
        title: "SLA-driven Incident Resolution",
        description: "Rapid response and resolution for critical production issues, adhering to strict Service Level Agreements."
      },
      {
        title: "Release Management",
        description: "Proactive analysis, testing, and adoption of features from Workday's biannual releases to keep you current."
      },
      {
        title: "Year-end Processing Support",
        description: "Expert guidance through tax form generation, filing, and year-end audits to ensure regulatory compliance."
      },
      {
        title: "Security Administration",
        description: "Maintenance of role-based security groups and domain security policies to protect sensitive data."
      },
      {
        title: "Root Cause Analysis",
        description: "Deep-dive investigation into recurring issues to provide permanent fixes rather than temporary workarounds."
      }
    ],
    icon: "life-buoy",
    imageUrl: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&q=80&w=2000"
  },
  {
    id: "integrations-reporting",
    title: "Integrations & Analytics",
    description: "Connecting your ecosystem and unlocking insights with Prism and Advanced Reporting.",
    longDescription: "Data needs to flow seamlessly between Workday and your third-party vendors (Benefits, GL, LMS). Our technical experts specialize in building robust Studio, EIB, and Core Connector integrations. Furthermore, we unlock the power of your data with advanced reporting and Prism Analytics. We build custom dashboards that provide executives with real-time insights into headcount, labor costs, retention, and operational KPIs.",
    features: [
      {
        title: "Custom Integrations",
        description: "Design and build complex integrations using Workday Studio, EIB, and Core Connector to link your ecosystem."
      },
      {
        title: "PECI, PICOF & Benefits Interfaces",
        description: "Standardized and custom interface management for payroll providers and benefit carriers."
      },
      {
        title: "Advanced Reporting & Dashboards",
        description: "Creation of matrix reports, composite reports, and executive dashboards to visualize key performance indicators."
      },
      {
        title: "BIRT Document Design",
        description: "Development of pixel-perfect business documents for offer letters, invoices, and statements."
      },
      {
        title: "Prism Analytics",
        description: "Ingest and blend external data with Workday data to create comprehensive analytical datasets."
      }
    ],
    icon: "bar-chart",
    imageUrl: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=2000"
  }
];

export const SYSTEM_INSTRUCTION = `You are a helpful and professional AI consultant for ${COMPANY_INFO.name}.
Your goal is to assist visitors with information about our Workday consulting services.
We specialize in Workday HCM (Human Capital Management) and Payroll implementation and support.
Our address is ${COMPANY_INFO.fullAddress}.
Our domain is ${COMPANY_INFO.domain}.
Be polite, concise, and professional, matching the tone of a high-end consulting firm like LRB Group or Alku.
Do not make up services we do not offer. Focus on Workday HCM, Payroll, Support, Implementation, and Integrations.
If asked for pricing, suggest they contact us via the form or email for a tailored quote.`;